import React, { useState } from 'react'

// Use lucide as fallback since we have it in deps
import {
  Plus, Trash2, Pencil, Check, X, MessageSquare, Menu, Sparkles
} from 'lucide-react'

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const now = new Date()
  const diffDays = Math.floor((now - d) / 86400000)
  if (diffDays === 0) return 'Danas'
  if (diffDays === 1) return 'Juče'
  if (diffDays < 7) return d.toLocaleDateString('en', { weekday: 'long' })
  return d.toLocaleDateString('en', { month: 'short', day: 'numeric' })
}

const groupByDate = (conversations) => {
  const groups = {}
  conversations.forEach(c => {
    const label = formatDate(c.updatedAt)
    if (!groups[label]) groups[label] = []
    groups[label].push(c)
  })
  return groups
}

export default function Sidebar({
  conversations,
  activeId,
  onSelect,
  onCreate,
  onDelete,
  onRename,
  collapsed,
  onToggle,
}) {
  const [editingId, setEditingId] = useState(null)
  const [editTitle, setEditTitle] = useState('')
  const [hoveredId, setHoveredId] = useState(null)

  const startEdit = (e, conv) => {
    e.stopPropagation()
    setEditingId(conv.id)
    setEditTitle(conv.title)
  }

  const confirmEdit = async (e, id) => {
    e?.stopPropagation()
    if (editTitle.trim()) await onRename(id, editTitle.trim())
    setEditingId(null)
  }

  const cancelEdit = (e) => {
    e?.stopPropagation()
    setEditingId(null)
  }

  const handleDelete = (e, id) => {
    e.stopPropagation()
    if (window.confirm('Delete this conversation?')) onDelete(id)
  }

  const groups = groupByDate(conversations)

  return (
    <>
      {/* Sidebar */}
      <aside style={{
        width: collapsed ? '0' : 'var(--sidebar-w)',
        minWidth: collapsed ? '0' : 'var(--sidebar-w)',
        background: 'var(--bg-sidebar)',
        borderRight: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'width 0.25s ease, min-width 0.25s ease',
        position: 'relative',
      }}>
        <div style={{ width: 'var(--sidebar-w)', height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <div style={{
            padding: '12px 12px 8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <button
              onClick={onToggle}
              style={iconBtn}
              title="Close sidebar"
            >
              <Menu size={18} />
            </button>

            <button
              onClick={onCreate}
              style={{
                ...iconBtn,
                marginLeft: 'auto',
              }}
              title="Nova poruka"
            >
              <Plus size={18} />
            </button>
          </div>

          {/* New Chat button */}
          <div style={{ padding: '10px 10px 4px' }}>
            <button
              onClick={onCreate}
              style={{
                width: '100%',
                padding: '9px 12px',
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: 13,
                fontFamily: 'var(--font-sans)',
                transition: 'background var(--transition)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <Plus size={15} />
              Nova poruka
            </button>
          </div>

          {/* Conversation list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 10px 10px' }}>
            {conversations.length === 0 ? (
              <div style={{
                padding: '40px 16px',
                textAlign: 'center',
                color: 'var(--text-muted)',
                fontSize: 13,
              }}>
              </div>
            ) : (
              Object.entries(groups).map(([label, convs]) => (
                <div key={label}>
                  <div style={{
                    padding: '10px 8px 4px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}>
                    {label}
                  </div>
                  {convs.map(conv => (
                    <ConversationItem
                      key={conv.id}
                      conv={conv}
                      isActive={conv.id === activeId}
                      isEditing={editingId === conv.id}
                      editTitle={editTitle}
                      onEditChange={setEditTitle}
                      onSelect={() => onSelect(conv.id)}
                      onStartEdit={startEdit}
                      onConfirmEdit={confirmEdit}
                      onCancelEdit={cancelEdit}
                      onDelete={handleDelete}
                      isHovered={hoveredId === conv.id}
                      onHover={setHoveredId}
                    />
                  ))}
                </div>
              ))
            )}
          </div>

        </div>
      </aside>

      {/* Collapsed toggle button */}
      {collapsed && (
        <button
          onClick={onToggle}
          style={{
            position: 'fixed',
            top: 12,
            left: 12,
            zIndex: 100,
            ...iconBtn,
          }}
        >
          <Menu size={18} />
        </button>
      )}
    </>
  )
}

function ConversationItem({
  conv, isActive, isEditing, editTitle, onEditChange,
  onSelect, onStartEdit, onConfirmEdit, onCancelEdit,
  onDelete, isHovered, onHover,
}) {
  return (
    <div
      onClick={onSelect}
      onMouseEnter={() => onHover(conv.id)}
      onMouseLeave={() => onHover(null)}
      style={{
        position: 'relative',
        padding: '9px 10px',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        background: isActive ? 'var(--bg-active)' : isHovered ? 'var(--bg-hover)' : 'transparent',
        transition: 'background var(--transition)',
        marginBottom: 1,
      }}
    >
      {isEditing ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={e => e.stopPropagation()}>
          <input
            autoFocus
            value={editTitle}
            onChange={e => onEditChange(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') onConfirmEdit(e, conv.id)
              if (e.key === 'Escape') onCancelEdit(e)
            }}
            style={{
              flex: 1,
              background: 'var(--bg-input)',
              border: '1px solid var(--accent)',
              borderRadius: 4,
              padding: '3px 6px',
              color: 'var(--text-primary)',
              fontSize: 13,
              fontFamily: 'var(--font-sans)',
              outline: 'none',
            }}
          />
          <button onClick={e => onConfirmEdit(e, conv.id)} style={smallBtn(true)}>
            <Check size={12} />
          </button>
          <button onClick={onCancelEdit} style={smallBtn(false)}>
            <X size={12} />
          </button>
        </div>
      ) : (
        <>
          <div style={{
            fontSize: 13,
            fontWeight: isActive ? 500 : 400,
            color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            paddingRight: isHovered || isActive ? 52 : 0,
          }}>
            {conv.title}
          </div>
          {conv.lastMessage && (
            <div style={{
              fontSize: 11,
              color: 'var(--text-muted)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              marginTop: 2,
            }}>
              {conv.lastMessage}
            </div>
          )}

          {/* Action buttons */}
          {(isHovered || isActive) && (
            <div style={{
              position: 'absolute',
              right: 6,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              gap: 2,
            }}>
              <button onClick={e => onStartEdit(e, conv)} style={actionBtn}>
                <Pencil size={13} />
              </button>
              <button onClick={e => onDelete(e, conv.id)} style={{ ...actionBtn, color: '#ef4444' }}>
                <Trash2 size={13} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ── Button styles ─────────────────────────────────────────────

const iconBtn = {
  background: 'transparent',
  border: 'none',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  padding: '6px',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background var(--transition), color var(--transition)',
}

const actionBtn = {
  background: 'transparent',
  border: 'none',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  padding: '4px',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
}

const smallBtn = (isConfirm) => ({
  background: 'transparent',
  border: 'none',
  color: isConfirm ? 'var(--accent)' : 'var(--text-muted)',
  cursor: 'pointer',
  padding: '3px',
  display: 'flex',
  alignItems: 'center',
})
